import toast from "react-hot-toast";
import { API_ERROR_MESSAGES } from "./apiErrorMessages";

/**
 * The one way to turn a failed API call into text for the user (SFRESTO-151,
 * SFRESTO-163). scanifood_api sends raw translation keys (violation
 * `message`s, `detail`, `hydra:description`, `description`, or `message` for
 * security errors) and, for framework errors, English prose ("Access
 * Denied.", type errors, "… not found"). Neither may reach the user: a known
 * key becomes its French text (`API_ERROR_MESSAGES`, or the caller's own
 * `messages`), anything else falls back to French copy.
 */

export const GENERIC_ERROR_MESSAGE =
  "Une erreur est survenue. Veuillez réessayer.";

export const FORBIDDEN_MESSAGE =
  "Vous n'avez pas l'autorisation d'effectuer cette action.";

export const NETWORK_ERROR_MESSAGE =
  "Impossible de joindre le serveur. Vérifiez votre connexion internet puis réessayez.";

export interface ApiErrorOptions {
  /** French text for a 403 that carries no known key. */
  forbidden?: string;
  /** Context-specific French text, checked before the shared map. */
  messages?: Record<string, string>;
}

type ApiErrorData = {
  violations?: { propertyPath?: string; message?: unknown }[];
  detail?: unknown;
  "hydra:description"?: unknown;
  description?: unknown;
  message?: unknown;
};

// A dotted translation key, e.g. "validation.purchase_order.error.already_paid".
const KEY_PATTERN = /[a-z][a-z0-9_]*(?:\.[a-zA-Z0-9_]+)+/g;

const NETWORK_STATUSES = new Set(["FETCH_ERROR", "TIMEOUT_ERROR"]);

/** Every text of the error body that may hold a key, most specific first. */
function errorTexts(error: unknown): string[] {
  const data = (error as { data?: unknown } | null | undefined)?.data;
  if (!data || typeof data !== "object") return [];

  const body = data as ApiErrorData;
  const violations = Array.isArray(body.violations) ? body.violations : [];
  return [
    ...violations.map((v) => v?.message),
    body.detail,
    body["hydra:description"],
    body.description,
    body.message,
  ].filter((t): t is string => typeof t === "string" && t !== "");
}

/** The translation keys an API error carries, in order, each once. */
export function getApiErrorKeys(error: unknown): string[] {
  const keys = errorTexts(error).flatMap((t) => t.match(KEY_PATTERN) ?? []);
  return [...new Set(keys)];
}

/** True when the API error carries this translation key. */
export function hasApiErrorKey(error: unknown, key: string): boolean {
  return getApiErrorKeys(error).includes(key);
}

/**
 * French text for one API string (a violation message or a `detail`), or
 * `undefined` when it holds no known key. Accepts "path: key" and multi-line
 * details.
 */
export function translateApiErrorText(
  text: string | null | undefined,
  messages?: Record<string, string>
): string | undefined {
  if (!text) return undefined;
  for (const key of text.match(KEY_PATTERN) ?? []) {
    const message = messages?.[key] ?? API_ERROR_MESSAGES[key];
    if (message) return message;
  }
  return undefined;
}

/**
 * French text for a failed API call (an RTK Query error or anything thrown by
 * `.unwrap()`). Order: a known key; then a French refusal for a 403, a
 * network message when the server could not be reached; else `fallback`.
 * Never returns a raw key or the server's English text.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string = GENERIC_ERROR_MESSAGE,
  { forbidden = FORBIDDEN_MESSAGE, messages }: ApiErrorOptions = {}
): string {
  for (const key of getApiErrorKeys(error)) {
    const message = messages?.[key] ?? API_ERROR_MESSAGES[key];
    if (message) return message;
  }

  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (status === 403) return forbidden;
  if (typeof status === "string" && NETWORK_STATUSES.has(status)) {
    return NETWORK_ERROR_MESSAGE;
  }
  return fallback;
}

/** `toast.error` with `getApiErrorMessage()`. */
export function toastApiError(
  error: unknown,
  fallback?: string,
  options?: ApiErrorOptions
): void {
  toast.error(getApiErrorMessage(error, fallback, options));
}
